// Phiếu IPQC (báo cáo kiểm tra công đoạn): Duyệt setup 4M → Ký mẫu → Cho sản xuất → Kiểm tra lặp lại → Chốt lô.
// Dùng chung web + máy chủ để giai đoạn, loại kiểm tra (cột cũ) và tổng chốt lô tính một cách.

/** 4M: Người · Máy · Vật tư · Phương pháp (nhóm chỉ tiêu `category` của mẫu IPQC) */
export const IPQC_FOUR_M_GROUPS = ['Người', 'Máy', 'Vật tư', 'Phương pháp'] as const;
export type IpqcFourMGroup = (typeof IPQC_FOUR_M_GROUPS)[number];
/** Nhóm chỉ tiêu sản phẩm (kiểm khi ký mẫu và lặp lại khi chạy) */
export const IPQC_PRODUCT_GROUP = 'Sản phẩm';

function plain(text: unknown): string {
  return String(text ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .trim()
    .toLowerCase();
}

const FOUR_M_KEYS: Record<string, IpqcFourMGroup> = {
  nguoi: 'Người',
  man: 'Người',
  may: 'Máy',
  machine: 'Máy',
  'vat tu': 'Vật tư',
  material: 'Vật tư',
  'phuong phap': 'Phương pháp',
  method: 'Phương pháp',
};

/** Nhóm 4M của một chỉ tiêu (null = không thuộc 4M) */
export function fourMGroupOf(category?: string | null): IpqcFourMGroup | null {
  return FOUR_M_KEYS[plain(category)] ?? null;
}

export function isFourMCategory(category?: string | null): boolean {
  return fourMGroupOf(category) !== null;
}

/** Thứ tự hiển thị 4M; ngoài 4M xếp sau */
export function fourMOrder(category?: string | null): number {
  const g = fourMGroupOf(category);
  return g ? IPQC_FOUR_M_GROUPS.indexOf(g) : IPQC_FOUR_M_GROUPS.length;
}

// ───────────────────────── Giai đoạn & loại kiểm tra ─────────────────────────

/** Mã giai đoạn lưu ở cột report_stage. Ký mẫu / Cho sản xuất nằm trong SETUP (có cột thời điểm riêng). */
export const IPQC_REPORT_STAGES = ['SETUP', 'PATROL_IN_PROGRESS', 'FINAL_BATCH'] as const;
export type IpqcReportStage = (typeof IPQC_REPORT_STAGES)[number];

const LEGACY_REPORT_STAGE: Record<string, IpqcReportStage> = {
  SETUP_PENDING: 'SETUP',
  SIGNOFF: 'SETUP',
  RELEASED: 'PATROL_IN_PROGRESS',
  IN_PATROL: 'PATROL_IN_PROGRESS',
  PATROL: 'PATROL_IN_PROGRESS',
  BATCH_COMPLETED: 'FINAL_BATCH',
};

/** Chuẩn hoá mã giai đoạn (đọc cả mã cũ của form) */
export function normalizeIpqcReportStage(value?: string | null): IpqcReportStage {
  if (!value) return 'FINAL_BATCH';
  const v = String(value).toUpperCase();
  if ((IPQC_REPORT_STAGES as readonly string[]).includes(v)) return v as IpqcReportStage;
  return LEGACY_REPORT_STAGE[v] || 'FINAL_BATCH';
}

export type IpqcCheckType = 'FIRST_PIECE' | 'ROUTINE' | 'PATROL' | 'LAST_PIECE';

/**
 * Cột check_type (giữ cho báo cáo / thống kê cũ) suy ra từ giai đoạn — một nguồn duy nhất là giai đoạn.
 * Duyệt setup → FIRST_PIECE; Kiểm tra lặp lại → PATROL; Chốt lô giữ loại trước đó.
 */
export function ipqcCheckTypeForStage(stage?: string | null, previous?: string | null): IpqcCheckType {
  const st = normalizeIpqcReportStage(stage);
  if (st === 'SETUP') return 'FIRST_PIECE';
  if (st === 'PATROL_IN_PROGRESS') return 'PATROL';
  const prev = String(previous || '').toUpperCase();
  return prev === 'ROUTINE' || prev === 'PATROL' || prev === 'LAST_PIECE' ? (prev as IpqcCheckType) : 'FIRST_PIECE';
}

// ───────────────────────── Kiểm tra lặp lại & chốt lô ─────────────────────────

export interface IpqcRound {
  roundNo: number;
  checkTime: string;
  runningQty: number;
  sampleQty: number;
  okQty: number;
  ngQty: number;
  defectCode?: string;
  /** Giá trị đo / kết quả theo chỉ tiêu "lặp lại" của mẫu: { [criterionId]: số đo | PASS | FAIL | NA } */
  values?: Record<string, string | number | null>;
  /** Phiếu cũ: số đo ghi dạng chữ */
  measuredReading?: string;
  result: 'OK' | 'ADJUST' | 'HOLD';
  note?: string;
}

function int(v: unknown, min = 0): number {
  const n = Math.round(Number(v));
  return Number.isFinite(n) ? Math.max(min, n) : min;
}

/** Chuẩn hoá các lần kiểm tra lặp lại (đọc cả khoá cũ sampleSize / ngCount / notes) */
export function normalizeIpqcRounds(raw: unknown): IpqcRound[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((r) => r && typeof r === 'object')
    .map((r: any, idx: number) => {
      const sampleQty = int(r.sampleQty ?? r.sampleSize ?? 5, 1);
      const ngQty = Math.min(sampleQty, int(r.ngQty ?? r.ngCount ?? 0));
      const values =
        r.values && typeof r.values === 'object' && !Array.isArray(r.values) ? (r.values as Record<string, string | number | null>) : undefined;
      const result: IpqcRound['result'] =
        r.result === 'HOLD' || r.result === 'FAILED' || r.result === 'FAIL' ? 'HOLD' : r.result === 'ADJUST' ? 'ADJUST' : 'OK';
      return {
        roundNo: int(r.roundNo ?? r.patrolIndex ?? r.seq ?? idx + 1, 1),
        checkTime: String(r.checkTime ?? r.checkedAt ?? ''),
        runningQty: int(r.runningQty ?? 0),
        sampleQty,
        okQty: Math.max(0, sampleQty - ngQty),
        ngQty,
        defectCode: r.defectCode ? String(r.defectCode) : '',
        ...(values ? { values } : {}),
        measuredReading: r.measuredReading ? String(r.measuredReading) : r.notes && !r.note ? String(r.notes) : '',
        result,
        note: r.note ? String(r.note) : '',
      };
    });
}

export interface IpqcLotTotals {
  /** Số lần kiểm tra lặp lại */
  rounds: number;
  /** Tổng mẫu = mẫu ký + Σ mẫu các lần */
  totalSampled: number;
  /** Tổng mẫu lỗi */
  totalNg: number;
  totalOk: number;
}

/** Tổng chốt lô tự tính từ mẫu ký mẫu + các lần kiểm tra lặp lại (không nhập tay) */
export function rollupIpqcLot(setupSampleQty: unknown, setupDefectQty: unknown, rounds: unknown): IpqcLotTotals {
  const list = normalizeIpqcRounds(rounds);
  const setupSample = int(setupSampleQty);
  const setupNg = Math.min(setupSample, int(setupDefectQty));
  const totalSampled = setupSample + list.reduce((s, r) => s + r.sampleQty, 0);
  const totalNg = setupNg + list.reduce((s, r) => s + r.ngQty, 0);
  return { rounds: list.length, totalSampled, totalNg, totalOk: Math.max(0, totalSampled - totalNg) };
}

/** Kết quả duyệt setup từ checklist 4M: có Không đạt → Chỉnh máy; còn chưa kiểm → null; còn lại Đạt */
export function ipqcSetupResultFrom4M(items: Array<{ result?: string | null; required?: boolean }> | null | undefined): 'PASSED' | 'ADJUST_REQUIRED' | null {
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return null;
  if (list.some((i) => String(i?.result || '').toUpperCase() === 'FAIL')) return 'ADJUST_REQUIRED';
  if (list.some((i) => i?.required !== false && ['', 'PENDING'].includes(String(i?.result || '').toUpperCase()))) return null;
  return 'PASSED';
}
